import React from "react";
import { Avatar, Button, Card, Popover } from "@/components/ui";
import { ExternalLinkIcon, PlusIcon, UsersIcon } from "lucide-react";
import { SocialMentionSearchResult } from "@/services/bedrock/dtos/Post";
import { useSearchContext } from "../SearchContext";

interface SocialMentionCardProps {
  result: SocialMentionSearchResult;
  onAdd: (result: SocialMentionSearchResult) => void;
  onOpenPublicLink: (url: string) => void;
}

export function SocialMentionCard({ result, onAdd, onOpenPublicLink }: SocialMentionCardProps) {
  const { players, selectedPlayer, handleBulkAdd } = useSearchContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const hasMultiplePlayers = players.length > 1;

  const handleAddClick = (e: React.MouseEvent) => {
    if (!hasMultiplePlayers) {
      onAdd(result);
    }
  };

  return (
    <Card
      key={result.externalId}
      className="flex flex-row items-center gap-3 p-3 hover:bg-slate-50 transition-colors cursor-pointer group shadow-none ring-1 ring-slate-100"
      onClick={handleAddClick}
    >
      <Avatar size="sm">
        <Avatar.Image src={result.profileImageUrl} />
        <Avatar.Fallback>{result.name[0]}</Avatar.Fallback>
      </Avatar>
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-bold truncate">{result.name}</span>
        <span className="text-xs text-muted-foreground truncate">{result.tag}</span>
      </div>
      <div className="flex items-center gap-1">
        {result.publicLink && (
          <Button
            size="xs"
            variant="ghost"
            className="size-8 p-0"
            onClick={e => {
              e.stopPropagation();
              onOpenPublicLink(result.publicLink!);
            }}
          >
            <ExternalLinkIcon className="size-3.5 text-slate-400 hover:text-primary transition-colors" />
          </Button>
        )}

        {hasMultiplePlayers ? (
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger
              render={
                <Button size="xs" variant="ghost" className="h-8 px-2" onClick={e => e.stopPropagation()}>
                  Add
                </Button>
              }
            />
            <Popover.Content align="end" className="w-fit max-w-[300px] p-1 gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="justify-start font-normal h-9 w-full"
                onClick={e => {
                  e.stopPropagation();
                  handleBulkAdd(result);
                  setIsOpen(false);
                }}
              >
                <UsersIcon className="size-3.5 mr-2 text-slate-400 shrink-0" />
                Add to <b>all</b> Players' posts
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start font-normal h-9 w-full overflow-hidden"
                onClick={e => {
                  e.stopPropagation();
                  onAdd(result);
                  setIsOpen(false);
                }}
              >
                <PlusIcon className="size-3.5 mr-2 text-slate-400 shrink-0" />
                <span className="truncate">Only selected Player's post</span>
              </Button>
            </Popover.Content>
          </Popover>
        ) : (
          <Button size="xs" variant="ghost">
            Add
          </Button>
        )}
      </div>
    </Card>
  );
}
